import { RooDateTime, RooDate } from "roo-time";
import { ACCOUNT_TYPE, TRANSACTION_STATUS } from "./DBEnums.js";
import { s_log } from "./services/log/index.js";
//
// INSTITUTION STATE PRIMITIVE MANIPULATION
//
export const DEFAULT_MEMBERSHIP = {
    c_aggregator_connected: true,
    c_connection_status_raw: "connected",
    c_last_refresh_from_institution_to_aggregator: new RooDate(),
};
export const DEFAULT_ACCOUNT = {
    source_id: "SimAccount_1",
    type: "CHECKING",
    c_name: "name",
    c_currency_code: "USD",
    c_account_last_4_digits: "0000",
    c_balance: 0.0,
    c_min_payment: null,
    c_interest_rate: null,
    c_closed: false,
};
export const DEFAULT_TRANSACTION = {
    source_id: "SimTransaction_1",
    accountSourceId: DEFAULT_ACCOUNT.source_id,
    c_amount: 0.0,
    c_currency_code: "USD",
    c_date: new RooDate(),
    c_description: "description",
    c_raw_category: "UNKNOWN",
    c_raw_subcategory: "UNKNOWN",
    c_status: "POSTED",
};
export function assertIsSimulatedMembershipState(obj) {
    if (obj === null || typeof obj !== "object")
        throw new Error("value is not an object");
    const EXP_KEYS = ["membership", "accounts", "transactions"];
    for (let key in obj) {
        if (!EXP_KEYS.includes(key))
            throw new Error(`value has extra key ${key}`);
    }
    for (let key of EXP_KEYS.values()) {
        if (!obj.hasOwnProperty(key))
            throw new Error(`value doesn't contain ${key}`);
    }
    const { membership, accounts, transactions } = obj;
    try {
        assertIsSimulatorMembership(membership);
    }
    catch (e) {
        throw new Error(`[membership] ${e}`);
    }
    try {
        assertIsArrayOf(accounts, assertIsSimulatorAccount);
    }
    catch (e) {
        throw new Error(`[accounts] ${e}`);
    }
    try {
        assertIsArrayOf(transactions, assertIsSimulatorTransaction);
    }
    catch (e) {
        throw new Error(`[transactions] ${e}`);
    }
    transactions.forEach((transaction, idx) => {
        if (!accounts.find((a) => a.source_id === transaction.accountSourceId))
            throw new Error(`transactions[${idx}] has unmatched accountSourceId`);
    });
    if (hasDuplicates(accounts.map((a) => a.source_id)))
        throw new Error(`accounts has duplicate source_id values`);
    if (hasDuplicates(transactions.map((t) => t.source_id)))
        throw new Error(`transactions has duplicate source_id values`);
}
function hasDuplicates(arr) {
    const firstDuplicate = arr.find((item, index) => arr.indexOf(item) !== index);
    return firstDuplicate !== undefined;
}
function assertIsArrayOf(obj, asserter) {
    if (!Array.isArray(obj))
        throw new Error("is not an array");
    let objArr = obj;
    objArr.forEach((v, idx) => {
        try {
            asserter(v);
        }
        catch (e) {
            throw new Error(`[${idx}] ${e}`);
        }
    });
}
export function assertIsSimulatorMembership(obj) {
    if (obj === null || typeof obj !== "object")
        throw new Error("value is not an object");
    for (let key in obj) {
        if (!(key in DEFAULT_MEMBERSHIP))
            throw new Error(`value has extra key ${key}`);
    }
    for (let key in DEFAULT_MEMBERSHIP) {
        if (!obj.hasOwnProperty(key))
            throw new Error(`value doesn't contain ${key}`);
        const val = obj[key];
        if (["c_aggregator_connected"].includes(key)) {
            if (typeof val !== "boolean")
                throw new Error(`${key} is not a boolean`);
        }
        else if (["c_last_refresh_from_institution_to_aggregator"].includes(key)) {
            if (!(val instanceof Date))
                throw new Error(`${key} is not a Date`);
        }
        else if (["c_connection_status_raw"].includes(key)) {
            if (val !== null && typeof val !== "string")
                throw new Error(`${key} is not a (string | null)`);
        }
        else {
            if (typeof val !== "string")
                throw new Error(`${key} is not a string`);
        }
    }
}
export function assertIsSimulatorAccount(obj) {
    if (obj === null || typeof obj !== "object")
        throw new Error("value is not an object");
    for (let key in obj) {
        if (!(key in DEFAULT_ACCOUNT))
            throw new Error(`value has extra key ${key}`);
    }
    for (let key in DEFAULT_ACCOUNT) {
        if (!obj.hasOwnProperty(key))
            throw new Error(`value doesn't contain ${key}`);
        const val = obj[key];
        if (["c_balance"].includes(key)) {
            if (typeof val !== "number")
                throw new Error(`${key} is not a number`);
        }
        else if (["c_min_payment"].includes(key)) {
            if (val !== null && typeof val !== "number")
                throw new Error(`${key} is not undefined or a number`);
            if (val && val < 0)
                throw new Error(`${key} must be non-negative`);
        }
        else if (["c_interest_rate"].includes(key)) {
            if (val !== null && typeof val !== "number")
                throw new Error(`${key} is not undefined or a number`);
            if (val && (val < 0 || val > 1))
                throw new Error(`${key} must be in the [0,1] range`);
        }
        else if (["c_closed"].includes(key)) {
            if (typeof val !== "boolean")
                throw new Error(`${key} is not a boolean`);
        }
        else {
            if (typeof val !== "string")
                throw new Error(`${key} is not a string`);
        }
        if (key === "type") {
            if (!ACCOUNT_TYPE.includes(val))
                throw new Error(`${key} is not an account_type (${ACCOUNT_TYPE})`);
        }
    }
}
export function assertIsSimulatorTransaction(obj) {
    if (obj === null || typeof obj !== "object")
        throw new Error("value is not an object");
    for (let key in obj) {
        if (!(key in DEFAULT_TRANSACTION))
            throw new Error(`value has extra key ${key}`);
    }
    for (let key in DEFAULT_TRANSACTION) {
        if (!obj.hasOwnProperty(key))
            throw new Error(`value doesn't contain ${key}`);
        const val = obj[key];
        if (["c_amount"].includes(key)) {
            if (typeof val !== "number")
                throw new Error(`${key} is not a number`);
        }
        else if (["c_date"].includes(key)) {
            if (!(val instanceof Date))
                throw new Error(`${key} is not a Date`);
        }
        else if (["c_status"].includes(key)) {
            if (!TRANSACTION_STATUS.includes(val))
                throw new Error(`${key} is not an transaction_status (${TRANSACTION_STATUS})`);
        }
        else {
            if (typeof val !== "string")
                throw new Error(`${key} is not a string`);
        }
    }
}
//
// INSTITUTION SIMULATED STATE MANAGEMENT
//
export class SimulatedAggregatorStateManager {
    constructor(p) {
        this.state = {};
        this.onChanged = p === null || p === void 0 ? void 0 : p.onChanged;
    }
    getMembershipManager(simConnectionId) {
        var _a;
        return (_a = this.state[simConnectionId]) !== null && _a !== void 0 ? _a : null;
    }
    getMembershipState(simConnectionId) {
        var _a, _b;
        return (_b = (_a = this.getMembershipManager(simConnectionId)) === null || _a === void 0 ? void 0 : _a.getState()) !== null && _b !== void 0 ? _b : null;
    }
    setMembershipState(simConnectionId, state) {
        var _a;
        this.state[simConnectionId] = new SimulatedMembershipStateManager(state);
        (_a = this.onChanged) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    wipeMembershipState(simConnectionId) {
        var _a;
        delete this.state[simConnectionId];
        (_a = this.onChanged) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    wipe() {
        var _a;
        this.state = {};
        (_a = this.onChanged) === null || _a === void 0 ? void 0 : _a.call(this);
    }
}
class SimulatedMembershipStateManager {
    constructor(state) {
        this.state = state;
    }
    getState() {
        return this.state;
    }
    setState(state) {
        this.state = state;
    }
    findAccountBySourceId(source_id) {
        return this.state.accounts.find((a) => a.source_id === source_id) || null;
    }
    findTransactionBySourceId(source_id) {
        return this.state.transactions.find((t) => t.source_id === source_id) || null;
    }
    updateAccountBalanceBySourceId(source_id, new_balance) {
        const simAccount = this.findAccountBySourceId(source_id);
        if (!simAccount)
            throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
                message: `Failed to find desired account in SimulatedAggregatorState`,
                lid: "r-4JwqnuyH",
                meta: { source_id },
            });
        simAccount.c_balance = new_balance;
    }
    addOrReplaceTransaction(newTransaction) {
        let idx = this.state.transactions.findIndex((t) => t.source_id === newTransaction.source_id);
        if (idx === -1) {
            this.state.transactions.push(newTransaction);
        }
        else {
            this.state.transactions[idx] = newTransaction;
        }
    }
    /* Doesn't wipe related transactions */
    deleteAccountsWhere(filterFn) {
        this.state.accounts = this.state.accounts.filter((x) => !filterFn(x));
    }
    deleteTransactionsWhere(filterFn) {
        this.state.transactions = this.state.transactions.filter((x) => !filterFn(x));
    }
}
//
// SCENARIO STATE MANIPULATION
//
export function scenarioToMembershipState(p) {
    let startDT = RooDateTime.fromJSDate(p.startTime);
    let stateManager = new SimulatedMembershipStateManager(initMembershipToScenario({ startTime: startDT, scenario: p.scenario }));
    const lastDayNumber = p.dayNumber || 0;
    // note that dayNumber 1 is when timeline[0] happens
    if (lastDayNumber < 0 || lastDayNumber > p.scenario.timeline.length) {
        throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
            message: "Invalid dayNumber",
            lid: "r-oSySIzyW",
            meta: { dayNumber: lastDayNumber, maxDayNumber: p.scenario.timeline.length - 1 },
        });
    }
    for (let dayNumber = 1; dayNumber <= lastDayNumber; dayNumber++) {
        const item = p.scenario.timeline[dayNumber - 1];
        const timeOfStep = startDT.plus({ days: dayNumber });
        updateMembershipToScenarioStep({ stateManager, timeOfStep, dayNumber, item });
    }
    return stateManager.getState();
}
function initMembershipToScenario(p) {
    const membership = fleshOutMembership(p.startTime);
    const accounts = p.scenario.initAccounts.map((a) => fleshOutAccount(a));
    const transactions = p.scenario.initTransactions.map((t, index) => {
        return fleshOutTransaction(p.startTime, index, t);
    });
    return { membership, accounts, transactions };
}
function updateMembershipToScenarioStep(p) {
    p.stateManager.getState().membership.c_last_refresh_from_institution_to_aggregator = p.timeOfStep.toJSDate();
    for (let a of p.item.accountBalances || []) {
        p.stateManager.updateAccountBalanceBySourceId(a.id, a.balance);
    }
    if (p.item.transactions) {
        p.item.transactions.forEach((t, idx) => p.stateManager.addOrReplaceTransaction(fleshOutTransaction(p.timeOfStep, idx, t)));
    }
    for (let deleteId of p.item.transactionDeletes || []) {
        p.stateManager.deleteTransactionsWhere((t) => t.source_id === deleteId);
    }
    for (let deleteId of p.item.accountDeletes || []) {
        p.stateManager.deleteAccountsWhere((a) => a.source_id === deleteId);
    }
}
function fleshOutMembership(startTime) {
    return Object.assign(Object.assign({}, DEFAULT_MEMBERSHIP), { c_last_refresh_from_institution_to_aggregator: startTime.toJSDate() });
}
function fleshOutAccount(partial) {
    return Object.assign(Object.assign({}, DEFAULT_ACCOUNT), { source_id: partial.id, c_name: partial.name, c_balance: partial.balance });
}
function fleshOutTransaction(timeOfStep, indexWithinDay, partial) {
    return fleshOutTransactionWithDate(timeOfStep.plus({ second: indexWithinDay }).toJSDate(), partial);
}
export function fleshOutTransactionWithDate(c_date, partial) {
    return Object.assign(Object.assign({}, DEFAULT_TRANSACTION), { source_id: partial.id, accountSourceId: partial.accountId, c_amount: partial.amount, c_date, c_description: partial.description, c_status: partial.status });
}
// TODO: Unused scrap function (formerly in loadOptions.ts).  Delete soon.
function simpleScenarioValidation(s) {
    const accountIds = s.initAccounts.map((a) => a.id);
    for (let x of s.timeline) {
        if (x.accountBalances) {
            for (let a of x.accountBalances) {
                if (!accountIds.includes(`_${a.id}`)) {
                    throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
                        message: "Unable to find accountId for scenario",
                        lid: "r-jCZWjOwq",
                        meta: { a },
                    });
                }
            }
        }
        if (x.transactions) {
            for (let t of x.transactions) {
                if (!accountIds.includes(`_${t.accountId}`)) {
                    throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
                        message: "Unable to find accountId for scenario",
                        lid: "r-qsIvsXRe",
                        meta: { t },
                    });
                }
            }
        }
    }
    // if (s.budgetInit) {
    //   for (let id of s.budgetInit.onBudgetAccounts) {
    //     if (!accountIds.includes(`_${id}`)) {
    //       throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
    //         message: "Unable to find accountId for scenario",
    //         lid: "r-yFovh1Xv",
    //         meta: { id },
    //       });
    //     }
    //   }
    //   for( let stack of s.budgetInit.budgetConfig.incomeSplitter.stack) {
    //     if(stack.type == 'percentPipe') {
    //       for
    //     }
    //   }
    //   if(s.budgetInit.budgetConfig.incomeSplitter.finalOverflowBucketId)
    // }
    return s;
}
//# sourceMappingURL=aggSimUtils.js.map